// src/layout/ExamStrip.tsx
//
// The ink strip above the nav: "GATE 2027 · Registration opens 28 Aug 2026
// · IIT Roorkee is the organising institute".
//
// Renders nothing at all when there is no cycle, no upcoming event, or the
// fetch failed. The countdown is context, not chrome — its absence should
// be invisible rather than leaving a hole.

import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import {
  getCurrentExamCycle,
  examKey,
  type ExamCycle,
  type ExamEvent,
} from "@/api/service/exam";

function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function describe(event: ExamEvent): string {
  if (event.kind === "window" && event.end_date) {
    return `${event.name} ${formatDate(event.start_date)} – ${formatDate(event.end_date)}`;
  }
  return `${event.name} ${formatDate(event.start_date)}`;
}

const DISMISS_KEY = "examStrip:dismissed";

function stripSignature(cycle: ExamCycle): string {
  const message = cycle.strip_message?.trim();
  return [
    cycle.cycle_id,
    message || cycle.next_event?.event_id || "",
    message ? "" : (cycle.next_event?.start_date ?? ""),
  ].join("|");
}

function readDismissed(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch {
    // Private mode in some browsers throws on storage access. Not being able
    // to remember the dismissal is not a reason to fail rendering the strip.
    return null;
  }
}

export function ExamStrip() {
  const [dismissedSignature, setDismissedSignature] = useState(readDismissed);

  const { data } = useQuery({
    queryKey: examKey,
    queryFn: getCurrentExamCycle,
    // The calendar changes a handful of times a year; there is no reason to
    // refetch it on every navigation.
    staleTime: 60 * 60 * 1000,
  });

  const cycle = data?.data;
  const next = cycle?.next_event;

  // show_strip is the admin off switch; `?? true` keeps the strip working
  // against a backend that predates it.
  if (!cycle || (cycle.show_strip ?? true) === false) return null;

  // A custom message stands on its own — it does not need a calendar event
  // behind it, which is the point of having it (results day, an outage note).
  const override = cycle.strip_message?.trim();
  if (!override && !next) return null;

  const signature = stripSignature(cycle);
  if (dismissedSignature === signature) return null;

  const dismiss = () => {
    try {
      window.sessionStorage.setItem(DISMISS_KEY, signature);
    } catch {
      /* storage blocked — it still hides for this render, which is the ask */
    }
    setDismissedSignature(signature);
  };

  const ctaLabel = cycle.strip_cta_label?.trim() || "All announcements →";
  const ctaUrl = cycle.strip_cta_url?.trim() || "/announcements";
  const ctaIsExternal = /^https?:\/\//i.test(ctaUrl);

  return (
    <div className="bg-ink text-ink-body">
      <div className="mx-auto flex w-full max-w-[1400px] items-center gap-3 px-4 py-2 text-[13px] sm:px-6">
        <span className="shrink-0 rounded border border-ink-border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-amber-bright">
          {cycle.name}
        </span>

        <span className="min-w-0 truncate">
          {override ? (
            override
          ) : (
            <>
              {describe(next!)}
              {/* Projected dates must never read as published fact. */}
              {!next!.is_confirmed && (
                <span className="ml-1.5 text-ink-muted">(expected)</span>
              )}
              {cycle.organising_institute && (
                <span className="hidden sm:inline">
                  {" · "}
                  {cycle.organising_institute} is the organising institute
                </span>
              )}
            </>
          )}
        </span>

        {ctaIsExternal ? (
          <a
            href={ctaUrl}
            target="_blank"
            rel="noreferrer"
            className="ml-auto hidden shrink-0 text-ink-body underline-offset-4 hover:underline sm:block"
          >
            {ctaLabel}
          </a>
        ) : (
          <Link
            to={ctaUrl}
            className="ml-auto hidden shrink-0 text-ink-body underline-offset-4 hover:underline sm:block"
          >
            {ctaLabel}
          </Link>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Hide this announcement for this tab"
          title="Hide for this tab"
          className="-my-1.5 -mr-1.5 ml-auto flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-ink-border/60 hover:text-ink-foreground sm:ml-1"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
